'use client'

import type { Chapter, Course, Video } from '@/payload-types'

import { CourseLayout } from './CourseLayout'
import { CourseVideo } from '@/components/Video/CourseVideo'
import RichText from '@/components/RichText'
import { formatPriceFromEuros } from '@/utilities/formatPriceFromEuros'
import { richTextToPlainText } from '@/utilities/richTextToPlainText'
import { BookOpenIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, PlayCircleIcon } from 'lucide-react'
import Link from 'next/link'

type Props = {
  course: Course
}

const defaultSummary =
  "Praktische coaching over voeding en leefstijl met videolessen en downloadbare schema's."

const INTRO_SLUG = 'introductie'

export const CoursePageContent = ({ course }: Props) => {
  const formattedPrice = formatPriceFromEuros(course.priceEuro)
  const overviewSummary = course.summary || richTextToPlainText(course.overview, 200) || defaultSummary
  const chapters = (course.chapters ?? []) as (Chapter | number)[]

  return (
    <div className="min-h-screen animate-in fade-in duration-300">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#085e41]/15 via-[#085e41]/5 to-background">
        <div className="mx-auto w-full max-w-6xl px-4 pb-14 pt-8 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <Link
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            href="/cursussen"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Alle cursussen
          </Link>

          <header className="mt-10 space-y-6">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#085e41]/20 bg-white/80 px-4 py-2 text-sm font-medium text-[#085e41] shadow-sm backdrop-blur-sm">
                <BookOpenIcon className="h-4 w-4" />
                Cursus
              </span>
              {formattedPrice ? (
                <span className="inline-flex items-center rounded-full bg-foreground/8 px-4 py-2 text-sm font-semibold text-foreground">
                  {formattedPrice}
                </span>
              ) : null}
            </div>

            <h1 className="text-balance text-4xl font-bold leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {course.title}
            </h1>

            {overviewSummary ? (
              <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground md:text-xl">
                {overviewSummary}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-6 pt-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <PlayCircleIcon className="h-4 w-4" />
                <span>{chapters.length} hoofdstukken</span>
              </div>
              <Link
                href={`/cursussen/${course.slug}/${INTRO_SLUG}`}
                className="group inline-flex items-center gap-2 rounded-full bg-[#085e41] px-7 py-3.5 font-semibold text-white shadow-lg shadow-[#085e41]/25 transition-all hover:gap-3 hover:bg-[#2D5A3D] hover:shadow-xl hover:shadow-[#085e41]/30"
              >
                Start cursus
                <ChevronRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </header>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 pb-32 pt-12 sm:px-6 lg:px-8">
        <div className="mt-0 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">
          {/* Sidebar navigation */}
          <aside className="lg:col-span-4">
            <nav
              aria-label="Hoofdstukken"
              className="sticky top-24 space-y-6 rounded-2xl border border-border/60 bg-card/50 p-6 shadow-sm backdrop-blur-sm"
            >
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Inhoud
                </p>
                <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {chapters.length + 1}
                </span>
              </div>
              <ol className="space-y-1">
                {/* Introduction item */}
                <li>
                  <Link
                    className="group flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted/50"
                    href={`/cursussen/${course.slug}/${INTRO_SLUG}`}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary">
                      ★
                    </span>
                    <span className="text-foreground/80 group-hover:text-foreground">
                      Introductie
                    </span>
                  </Link>
                </li>
                {/* Chapter items */}
                {chapters.map((c, i) => {
                  const chapter = typeof c === 'object' ? (c as Chapter) : null
                  if (!chapter) return null
                  return (
                    <li key={chapter.id ?? chapter.slug}>
                      <Link
                        className="group flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors hover:bg-muted/50"
                        href={`/cursussen/${course.slug}/${chapter.slug}`}
                      >
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary">
                          {i + 1}
                        </span>
                        <span className="text-foreground/80 group-hover:text-foreground">
                          {chapter.title}
                        </span>
                      </Link>
                    </li>
                  )
                })}
              </ol>
            </nav>
          </aside>

          {/* Main content */}
          <div className="lg:col-span-8 space-y-12 min-w-0">
            {/* Intro Video */}
            {course.introVideo && typeof course.introVideo === 'object' && (
              <section className="space-y-4">
                <CourseVideo
                  video={course.introVideo as Video}
                  className="rounded-2xl overflow-hidden shadow-lg"
                />
              </section>
            )}

            {/* Course Overview */}
            <section className="space-y-6">
              <h2 className="text-2xl font-bold text-foreground">Over deze cursus</h2>
              <div className="rounded-2xl border border-border/60 bg-card/30 p-8">
                {course.overview ? (
                  <RichText
                    data={course.overview}
                    className="prose prose-lg prose-gray max-w-none dark:prose-invert prose-headings:font-semibold prose-p:leading-relaxed"
                    enableGutter={false}
                  />
                ) : (
                  <p className="text-lg leading-relaxed text-muted-foreground">{overviewSummary}</p>
                )}
              </div>
            </section>

            {/* Course Chapters */}
            <section className="space-y-8">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-foreground">Hoofdstukken</h3>
                <span className="text-sm text-muted-foreground">{chapters.length} lessen</span>
              </div>
              <div className="space-y-4">
                {chapters.map((c, i) => {
                  const chapter = typeof c === 'object' ? (c as Chapter) : null
                  if (!chapter) return null
                  return (
                    <Link
                      key={chapter.id ?? i}
                      href={`/cursussen/${course.slug}/${chapter.slug}`}
                      className="group flex items-center gap-6 rounded-xl border border-border/60 bg-card/30 p-6 transition-all hover:border-primary/30 hover:bg-card/60 hover:shadow-md"
                    >
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-lg font-bold text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        {i + 1}
                      </div>
                      <div className="flex-1 space-y-1">
                        <h4 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {chapter.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Hoofdstuk {i + 1} van {chapters.length}
                        </p>
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  )
                })}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  )
}
